// 'serverDelta' query strategy. This strategy asks the server
// to send us what's changed from our current page of results.

// See serverQuery.ts for function documentation.

import * as _ from 'lodash';

import * as Filter from '../../util/filters';
import * as QueryTS from '../../util/queryts';
import {
  fromServerResultRunsLow,
  getLastUpdatedWithWindow,
  type ServerDeltaOp,
  type ServerResultDelta,
} from '../../util/runDeltas';
import {isServerGroupKey} from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import * as Api from './api';
import type * as ServerQuery from './serverQuery';
import type * as Types from './types';

export type ServerQueryDelta = Types.Query & {
  prevResult: {
    page: string[];
    maxUpdatedAt: string;
  };
};

function deltaKey(run: RunTypes.Run, query: Types.Query): string {
  return query.grouping?.some(isServerGroupKey) ? run.name : run.id;
}

function toServerQuery(
  query: Types.Query,
  prevResult: Types.QueryResult
): ServerQueryDelta {
  return {
    ...query,
    prevResult: {
      maxUpdatedAt: getLastUpdatedWithWindow(prevResult.lastUpdatedAt),
      page: prevResult.runs.map(r => deltaKey(r, query)),
    },
  };
}

function doQueryLocal(db: RunTypes.Run[], query: ServerQueryDelta) {
  let runs = Filter.filterRuns(query.filters, db);
  runs = QueryTS.groupRuns(query.grouping, runs);
  runs = QueryTS.sortRuns(query.sort, runs);
  runs = runs.slice(0, query.limit);
  // Within each run, filter out history that doesn't match the keys requested by the query.
  runs = runs.map(r => {
    const queryKeys = query.historySpecs?.flatMap(hs => hs.keys) ?? null;
    if (queryKeys) {
      let filteredHistory = r.sampledHistory?.map(sh =>
        sh.map(shs => _.pick(shs, queryKeys))
      );
      filteredHistory = filteredHistory?.filter(fh =>
        fh.every(fhs => {
          const histKeys = _.keys(_.omit(fhs, '_step'));
          return histKeys.length > 0;
        })
      );
      return {...r, sampledHistory: filteredHistory};
    }
    return r;
  });

  const ops: ServerDeltaOp[] = [];
  // local working copy; the splices below must never mutate the input page
  const page = query.prevResult.page.slice();

  // Make delete ops. Iterate from back so indices are correct after each delete.
  const delIndexes: number[] = [];
  _.forEachRight(page, (pi, i) => {
    if (_.findIndex(runs, r => pi === deltaKey(r, query)) !== i) {
      ops.push({op: 'DELETE', index: i});
      delIndexes.push(i);
    }
  });
  for (const i of delIndexes) {
    page.splice(i, 1);
  }

  // Then inserts
  _.forEach(runs, (r, i) => {
    if (_.find(page, pi => pi === deltaKey(r, query)) == null) {
      ops.push({op: 'INSERT', index: i, run: r});
      page.splice(i, 0, deltaKey(r, query));
    } else if (r.updatedAt > query.prevResult.maxUpdatedAt) {
      ops.push({op: 'UPDATE', index: i, run: r});
    }
  });

  _.zip(runs, page).forEach(([r, pi]) => {
    if (r == null || pi == null) {
      throw new Error('Invalid update alg');
    }
    if (deltaKey(r, query) !== pi) {
      throw new Error('Invalid update alg');
    }
  });

  // Then updates
  return {totalRuns: db.length, delta: ops};
}

// Boilerplate

export const TransformPostFilter: ServerQuery.Transform<
  ServerQueryDelta,
  ServerResultDelta
> = {
  toServerQuery,
  fromServerResult: fromServerResultRunsLow,
};

export const STRATEGY_GRAPHQL: ServerQuery.StrategyGraphql<
  ServerQueryDelta,
  ServerResultDelta
> = {
  ...TransformPostFilter,
  doQuery: Api.doDeltaQuery,
};

/** This is no longer in use */
export const DEPRECATED_STRATEGY_LOCAL: ServerQuery.StrategyLocal<
  ServerQueryDelta,
  ServerResultDelta
> = {
  ...TransformPostFilter,
  doQuery: doQueryLocal,
};
